/**
 * Domain enums — locked in Docs/IMPLEMENTATION_PLAN_AWS.md §23.
 * These are intentionally narrow string-literal unions so they can be
 * used as DynamoDB attribute values, JSON DTOs, and React enums alike.
 */

export const SERVICE_TYPES = [
  'ac',
  'plumbing',
  'electrical',
  'painting',
  'handyman',
  'carpentry',
  'water_tank',
  'tiling',
] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

/** Blog post lifecycle. See blog-implementation.md §3.3. */
export const POST_STATUSES = ['draft', 'scheduled', 'published', 'archived'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

/** Build modes for the blog Build Lambda. */
export const BUILD_MODES = ['incremental', 'full'] as const;
export type BuildMode = (typeof BUILD_MODES)[number];

export const SERVICE_LABELS: Record<ServiceType, { en: string; ar: string }> = {
  ac:          { en: 'AC',          ar: 'تكييف' },
  plumbing:    { en: 'Plumbing',    ar: 'سباكة' },
  electrical:  { en: 'Electrical',  ar: 'كهرباء' },
  painting:    { en: 'Painting',    ar: 'دهان' },
  handyman:    { en: 'Handyman',    ar: 'صيانة عامة' },
  carpentry:   { en: 'Carpentry',   ar: 'نجارة' },
  water_tank:  { en: 'Water Tank',  ar: 'خزان مياه' },
  tiling:      { en: 'Tiling',      ar: 'بلاط' },
};

export const JOB_STATUSES = [
  'draft',
  'scheduled',
  'assigned',
  'accepted',
  'on_my_way',
  'in_progress',
  'completed',
  'cancelled',
  'on_hold',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** Human-readable status labels (EN + AR) for badges and timelines. */
export const STATUS_LABELS: Record<JobStatus, { en: string; ar: string }> = {
  draft:       { en: 'Draft',       ar: 'مسودة' },
  scheduled:   { en: 'Scheduled',   ar: 'مجدول' },
  assigned:    { en: 'Assigned',    ar: 'تم التعيين' },
  accepted:    { en: 'Accepted',    ar: 'تم القبول' },
  on_my_way:   { en: 'On the way',  ar: 'في الطريق' },
  in_progress: { en: 'In progress', ar: 'قيد التنفيذ' },
  completed:   { en: 'Completed',   ar: 'مكتمل' },
  cancelled:   { en: 'Cancelled',   ar: 'ملغي' },
  on_hold:     { en: 'On hold',     ar: 'متوقف' },
};

/** Status badge colours — locked in §21 of IMPLEMENTATION_PLAN_AWS.md. */
export const STATUS_COLORS: Record<JobStatus, string> = {
  draft:       '#94A3B8',
  scheduled:   '#64748B',
  assigned:    '#2563EB',
  accepted:    '#4F46E5',
  on_my_way:   '#F97316',
  in_progress: '#F59E0B',
  completed:   '#16A34A',
  cancelled:   '#475569',
  on_hold:     '#A16207',
};

export const JOB_PRIORITIES = ['normal', 'urgent', 'emergency', 'vip'] as const;
export type JobPriority = (typeof JOB_PRIORITIES)[number];

export const COGNITO_GROUPS = ['super_admin', 'admin', 'dispatcher', 'worker', 'marketing'] as const;
export type CognitoGroup = (typeof COGNITO_GROUPS)[number];

/** Groups that can see CRM data (jobs, customers, workers, invoices). */
export const STAFF_GROUPS: readonly CognitoGroup[] = ['super_admin', 'admin', 'dispatcher'];
/** Groups that can manage blog content. `marketing` sees nothing else. */
export const CONTENT_GROUPS: readonly CognitoGroup[] = ['super_admin', 'marketing'];

export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contractor'] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMIRATES = [
  'dubai',
  'abu_dhabi',
  'sharjah',
  'ajman',
  'umm_al_quwain',
  'ras_al_khaimah',
  'fujairah',
] as const;
export type Emirate = (typeof EMIRATES)[number];

/** Locked SLA timers — see Docs/IMPLEMENTATION_PLAN_AWS.md §23. */
export const SLA_MINUTES = {
  urgentUnassigned: 10,
  emergencyUnstarted: 15,
  vipDelay: 5,
  webPushAckWindow: 5,
} as const;

/** Working hours (Asia/Dubai). Emergency jobs ignore these. */
export const WORKING_HOURS_GST = { start: '08:00', end: '22:00' } as const;
